// src/components/Contact.js
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import ExperienceBox from './ExperienceBox'; 
import ThemeContext from '../context/ThemeContext';
import Button from '../components/Button';
import InputField from '../components/InputField';

const ContactContainer = styled.div`
  max-width: 600px;
  margin: 50px auto;
  padding: 40px;
  background: ${({ theme }) => theme.mainBody};
  color: ${({ theme }) => theme.mainText};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const Title = styled.h1`
  text-align: center;
  color: #333;
`;

const Label = styled.label`
  display: block;
  margin: 10px 0 5px;
  font-weight: bold;
`;

const MessageContainer = styled.div`
  text-align: center;
  padding: 20px;
  font-size: 16px;
  font-weight: bold;
`;

const SuccessMessage = styled(MessageContainer)`
  color: green;
`;

const ErrorMessage = styled(MessageContainer)`
  color: red;
`;

const Contact = () => {
  const { theme } = useContext(ThemeContext);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageStage, setMessageStage] = useState(''); 

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted');
    setIsSubmitting(true);
    setSubmissionError(false);
    setMessageStage(''); // Clear any previous message stage

    // Create request payload
    const requestPayload = { name, email, message };
    const awsEndpoint = process.env.REACT_APP_AWS_ENDPOINT;
    const awsKey = process.env.REACT_APP_AWS_KEY;

    fetch(awsEndpoint, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': awsKey,
     },
      body: JSON.stringify(requestPayload),
    })
      .then((response) => {
        console.log('Received response:', response);
        if (!response.ok) {
          console.error('Failed response:', response.statusText);
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(() => {
        setSubmitted(true);
        setMessageStage('success'); 
        console.log('Submission successful');
        setName('');
        setEmail('');
        setMessage('');
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.error('Error:', error);
        setSubmissionError(true);
        setMessageStage('error'); 
        setIsSubmitting(false);
      });
  };

  return (
    <ContactContainer>
      <Title>Contact Me</Title>
      {submitted ? (
        <ExperienceBox
          messages={[
            "Request submitted successfully!",
            "Data is being processed.",
            "Please wait while we confirm your request.",
            "Request stored in AWS S3 bucket."
          ]}
          autoplay={true}
          duration={2000}
        />
      ) : (
        <form onSubmit={handleSubmit}>
          <Label>Name</Label>
          <InputField
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={isSubmitting || submitted}
          />
          <Label>Email</Label>
          <InputField
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting || submitted}
          />
          <Label>Reason for Contact</Label>
          <InputField
            isTextarea={true}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            disabled={isSubmitting || submitted}
          />
          <Button type="submit" disabled={isSubmitting || submitted}>
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      )}
      {messageStage === 'success' && (
        <SuccessMessage>
          Thank you for your request! I'll get back to you soon.
        </SuccessMessage>
      )}
      {messageStage === 'error' && (
        <ErrorMessage>
          There was an error submitting your request. Please try again later.
        </ErrorMessage>
      )}
    </ContactContainer>
  );
};

export default Contact;
